/**
 * Horse List Component
 * Displays a list of horses with filtering and management options
 * Reusable for both manager and player views
 */
import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { Horse, HorseFormData, Player } from "./types";
import HorseCard from "./HorseCard";
import HorseFormModal from "./HorseFormModal";

interface HorseListProps {
  devPort: string;
  clubId: number;
  managerMode: number;
  // Filtering
  ownerFilter?: "all" | "club" | "player";
  ownerId?: string | number; // Filter to specific owner
  // Display options
  showFilters?: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  emptyMessage?: string;
  // Data (can be passed in to avoid duplicate fetches)
  horses?: Horse[];
  players?: Player[];
  loading?: boolean;
  // Callbacks
  onHorsesChange?: () => void;
  // User-level horses (reusable across clubs)
  isUserLevel?: boolean;
}

function HorseList({
  devPort,
  clubId,
  managerMode,
  ownerFilter = "all",
  ownerId,
  showFilters = true,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  emptyMessage,
  horses = [],
  players = [],
  loading = false,
  onHorsesChange,
  isUserLevel = false,
}: HorseListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "available" | "unavailable">("all");

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [horseToDelete, setHorseToDelete] = useState<Horse | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const isManager = managerMode === -1;

  // Get owner name for a horse
  const getOwnerName = (horse: Horse): string | undefined => {
    if (horse.owner_type === "club") return undefined;
    const player = players.find(
      (p) => String(p.player_id) === String(horse.owner_id)
    );
    return player ? `${player.first_name} ${player.last_name}` : undefined;
  };

  // Filter horses
  const filteredHorses = horses.filter((horse) => {
    const matchesSearch =
      horse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (horse.notes && horse.notes.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" || horse.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Handlers
  const handleAddHorse = () => {
    setSelectedHorse(null);
    setFormModalOpen(true);
  };

  const handleEditHorse = (horse: Horse) => {
    setSelectedHorse(horse);
    setFormModalOpen(true);
  };

  const handleDeleteClick = (horse: Horse) => {
    setHorseToDelete(horse);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!horseToDelete) return;

    // Use user-level endpoint for player-owned horses, club endpoint for club horses
    const isPlayerHorse = horseToDelete.owner_type === "player" || isUserLevel;
    const endpoint = isPlayerHorse ? "horses/user/delete" : "horses/delete";
    const body = isPlayerHorse
      ? { user_id: horseToDelete.owner_id, horse_id: horseToDelete.id }
      : { club_id: clubId, horse_id: horseToDelete.id };

    fetch(devPort + endpoint, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then(() => {
        setSnackbar({
          open: true,
          message: `${horseToDelete.name} has been deleted`,
          severity: "success",
        });
        if (onHorsesChange) onHorsesChange();
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Failed to delete horse",
          severity: "error",
        });
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setHorseToDelete(null);
      });
  };

  const handleSaveHorse = (data: HorseFormData) => {
    // Determine if this is a user-level horse (player-owned, reusable across clubs)
    const isPlayerHorse = isUserLevel || (!isManager && ownerFilter === "player");
    const userId = isManager ? ownerId : managerMode;
    
    let endpoint: string;
    let body: Record<string, unknown>;
    
    if (selectedHorse) {
      // Update existing horse (uses same endpoint for both)
      endpoint = "horses/update";
      body = { ...data, club_id: clubId, horse_id: selectedHorse.id };
    } else if (isPlayerHorse && userId) {
      // Add new user-level horse
      endpoint = "horses/user/add";
      body = { ...data, user_id: String(userId) };
    } else {
      // Add new club horse
      endpoint = "horses/add";
      const ownerData = isManager 
        ? { owner_id: data.owner_id, owner_type: data.owner_type }
        : { owner_id: String(managerMode), owner_type: "player" as const };
      body = { ...data, ...ownerData, club_id: clubId };
    }

    fetch(devPort + endpoint, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then(() => {
        setSnackbar({
          open: true,
          message: selectedHorse
            ? `${data.name} has been updated`
            : `${data.name} has been added`,
          severity: "success",
        });
        if (onHorsesChange) onHorsesChange();
        setFormModalOpen(false);
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Failed to save horse",
          severity: "error",
        });
      });
  };

  if (loading) {
    return <LinearProgress />;
  }

  const defaultEmptyMessage = canAdd
    ? "No horses yet. Click 'Add Horse' to get started."
    : "No horses available.";

  return (
    <Box>
      <Stack spacing={2}>
        {/* Header with Add button */}
        {canAdd && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddHorse}
              size="small"
            >
              Add Horse
            </Button>
          </Box>
        )}

        {/* Filters */}
        {showFilters && horses.length > 0 && (
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search horses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) =>
                        setStatusFilter(e.target.value as "all" | "available" | "unavailable")
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="unavailable">Unavailable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Horse Stats */}
        {horses.length > 0 && (
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Total: {horses.length}
            </Typography>
            <Typography variant="body2" color="success.main">
              Available: {horses.filter((h) => h.status === "available").length}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Unavailable: {horses.filter((h) => h.status === "unavailable").length}
            </Typography>
          </Stack>
        )}

        {/* Horse Grid */}
        {filteredHorses.length === 0 ? (
          <Alert severity="info" variant="outlined">
            {horses.length === 0
              ? emptyMessage || defaultEmptyMessage
              : "No horses match your search criteria."}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredHorses.map((horse) => (
              <Grid key={horse.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <HorseCard
                  horse={horse}
                  ownerName={getOwnerName(horse)}
                  onEdit={canEdit ? () => handleEditHorse(horse) : undefined}
                  onDelete={canDelete ? () => handleDeleteClick(horse) : undefined}
                  managerMode={managerMode}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      {/* Add/Edit Modal */}
      <HorseFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSave={handleSaveHorse}
        horse={selectedHorse}
        devPort={devPort}
        clubId={clubId}
        isPlayerOwned={!isManager || ownerFilter === "player"}
        playerId={isManager ? ownerId : managerMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Horse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{horseToDelete?.name}"? This action
            cannot be undone and will remove all assignments for this horse.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default HorseList;
